/**
 * Campos reutilizáveis do formulário.
 *
 * Componentes pequenos e sem estado, para que a tela do construtor fique legível e a aparência seja
 * consistente sem repetir classes do Tailwind em cada campo.
 */

import type { ReactNode } from 'react';

const CLASSE_ENTRADA =
  'mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm outline-none transition-colors focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/30 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500';

export function Secao({
  titulo,
  descricao,
  children,
}: {
  readonly titulo: string;
  readonly descricao?: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
      <h2 className="border-b border-gray-100 pb-3 text-lg font-bold text-brand-blue">{titulo}</h2>
      {descricao !== undefined && <p className="mt-2 text-sm text-gray-500">{descricao}</p>}
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

export function Texto({
  id,
  rotulo,
  valor,
  onChange,
  dica,
  obrigatorio = false,
  multilinha = false,
  placeholder,
  rotuloOculto = false,
}: {
  readonly id: string;
  readonly rotulo: string;
  readonly valor: string;
  readonly onChange: (v: string) => void;
  readonly dica?: string;
  readonly obrigatorio?: boolean;
  readonly multilinha?: boolean;
  readonly placeholder?: string;
  /**
   * Esconde o rótulo visualmente, mantendo-o para leitor de tela.
   *
   * Existe para lista de campos repetidos, onde o cabeçalho da coluna já diz o que é e repetir o
   * rótulo em cada linha vira ruído. A alternativa — passar rótulo vazio — produziria um `<label>` sem
   * texto, que é pior que redundância: deixa o campo sem nome acessível.
   */
  readonly rotuloOculto?: boolean;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className={rotuloOculto ? 'sr-only' : 'block text-sm font-medium text-brand-blue'}
      >
        {rotulo}
        {obrigatorio && <span className="ml-1 text-red-500">*</span>}
      </label>
      {multilinha ? (
        <textarea
          id={id}
          value={valor}
          rows={3}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          className={CLASSE_ENTRADA}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={valor}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          className={CLASSE_ENTRADA}
        />
      )}
      {dica !== undefined && <p className="mt-1 text-xs text-gray-500">{dica}</p>}
    </div>
  );
}

/**
 * Campo numérico.
 *
 * Aceita vírgula como separador decimal, porque é o que o teclado brasileiro produz, e devolve `null`
 * quando vazio — para o domínio distinguir "não informado" de "zero".
 */
export function Numero({
  id,
  rotulo,
  valor,
  onChange,
  dica,
  passo = '0.01',
  desabilitado = false,
  obrigatorio = false,
  prefixo,
  rotuloOculto = false,
}: {
  readonly id: string;
  readonly rotulo: string;
  readonly valor: number | null;
  readonly onChange: (v: number | null) => void;
  readonly dica?: string;
  readonly passo?: string;
  readonly desabilitado?: boolean;
  readonly obrigatorio?: boolean;
  readonly prefixo?: string;
  /** Mesmo propósito do `rotuloOculto` de `Texto`: lista de campos repetidos. */
  readonly rotuloOculto?: boolean;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className={rotuloOculto ? 'sr-only' : 'block text-sm font-medium text-brand-blue'}
      >
        {rotulo}
        {obrigatorio && <span className="ml-1 text-red-500">*</span>}
      </label>
      <div className="relative">
        {prefixo !== undefined && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
            {prefixo}
          </span>
        )}
        <input
          id={id}
          type="number"
          step={passo}
          min="0"
          disabled={desabilitado}
          value={valor === null ? '' : String(valor)}
          onChange={(e) => {
            const bruto = e.target.value.replace(',', '.').trim();
            if (bruto === '') {
              onChange(null);
              return;
            }
            const n = Number(bruto);
            onChange(Number.isFinite(n) ? n : null);
          }}
          className={CLASSE_ENTRADA + (prefixo === undefined ? '' : ' pl-12')}
        />
      </div>
      {dica !== undefined && <p className="mt-1 text-xs text-gray-500">{dica}</p>}
    </div>
  );
}

export function Selecao<T extends string | number>({
  id,
  rotulo,
  valor,
  opcoes,
  onChange,
  dica,
}: {
  readonly id: string;
  readonly rotulo: string;
  readonly valor: T;
  readonly opcoes: readonly { readonly valor: T; readonly rotulo: string }[];
  readonly onChange: (v: T) => void;
  readonly dica?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-brand-blue">
        {rotulo}
      </label>
      <select
        id={id}
        value={String(valor)}
        onChange={(e) => {
          const escolhida = opcoes.find((o) => String(o.valor) === e.target.value);
          if (escolhida !== undefined) onChange(escolhida.valor);
        }}
        className={CLASSE_ENTRADA}
      >
        {opcoes.map((o) => (
          <option key={String(o.valor)} value={String(o.valor)}>
            {o.rotulo}
          </option>
        ))}
      </select>
      {dica !== undefined && <p className="mt-1 text-xs text-gray-500">{dica}</p>}
    </div>
  );
}

export function Marcador({
  id,
  rotulo,
  marcado,
  onChange,
  dica,
}: {
  readonly id: string;
  readonly rotulo: string;
  readonly marcado: boolean;
  readonly onChange: (v: boolean) => void;
  readonly dica?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="checkbox"
          checked={marcado}
          onChange={(e) => {
            onChange(e.target.checked);
          }}
          className="h-4 w-4 rounded border-gray-300 text-brand-accent focus:ring-brand-accent/40"
        />
        <label htmlFor={id} className="text-sm font-medium text-gray-900">
          {rotulo}
        </label>
      </div>
      {dica !== undefined && <p className="ml-6 mt-1 text-xs text-gray-500">{dica}</p>}
    </div>
  );
}

export function Aviso({
  tom,
  children,
}: {
  readonly tom: 'informacao' | 'atencao' | 'erro';
  readonly children: ReactNode;
}) {
  const estilo = {
    informacao: 'bg-blue-50 text-brand-blue border-brand-accent',
    atencao: 'bg-amber-50 text-amber-900 border-amber-400',
    erro: 'bg-red-50 text-red-800 border-red-500',
  }[tom];

  return (
    <div className={`rounded-r-lg border-l-4 px-4 py-3 text-sm leading-relaxed ${estilo}`}>
      {children}
    </div>
  );
}
